import React, {useEffect, useRef, useState} from "react";
import {
  Phone,
  User,
  Mail,
  MapPin,
  MessageCircle,
  Tag,
  Plus,
  Save,
  X,
  Trash2,
  Edit,
  AlertTriangle,
  Search,
  PhoneOutgoing,
} from "lucide-react";
import moment from "moment-timezone";

const DetailItem = ({icon: Icon, label, value}) => (
  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg shadow-sm">
    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#2ba9cd]" />
    <div className="flex flex-col">
      <span className="text-xs font-semibold uppercase text-gray-500">
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">
        {value || "N/A"}
      </span>
    </div>
  </div>
);

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

const FormInputField = ({
  label,
  name,
  type = "text",
  required = false,
  value,
  onChange,
  error,
}) => (
  <div className="flex flex-col space-y-1">
    <label htmlFor={name} className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value || ""}
      onChange={onChange}
      required={required}
      className={`p-2 border rounded-md focus:ring-indigo-500 transition duration-150 
                       ${
                         error
                           ? "border-red-500 focus:border-red-500"
                           : "border-gray-300 focus:border-indigo-500"
                       }`}
    />
    {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
  </div>
);

const ContactForm = ({contact, onSave, onCancel}) => {
  const [formData, setFormData] = useState(contact);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const {name, value} = e.target;
    setFormData((prev) => ({...prev, [name]: value}));
    // Clear error when user starts typing again
    setFormErrors((prev) => ({...prev, [name]: ""}));
  };

  const validate = (data) => {
    const errors = {};

    // Phone number validation: Must be present and follow a basic format
    const phonePattern = /^\+?[\d\s()+-]{7,20}$/; // Allows optional +, digits, spaces, (), -, minimum 7 characters

    if (!data.customer_phonenumber?.trim()) {
      errors.customer_phonenumber = "Phone number is required.";
    } else if (!phonePattern.test(data.customer_phonenumber.trim())) {
      errors.customer_phonenumber =
        "Please enter a valid phone number (min 7 digits, standard characters allowed).";
    }

    // Basic required field checks for core data
    if (!data.customer_firstname?.trim()) {
      errors.customer_firstname = "First Name is required.";
    }
    if (!data.customer_lastname?.trim()) {
      errors.customer_lastname = "Last Name is required.";
    }

    // Basic email format check
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      data.customer_email.trim() &&
      !emailPattern.test(data.customer_email.trim())
    ) {
      errors.customer_email = "Please enter a valid email address.";
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validate(formData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Optionally, focus on the first error field
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.focus();
      }
      return; // STOP submission if there are errors
    }

    onSave(formData);
  };

  const isNew = !contact.customer_phonenumber;

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col overflow-y-auto h-[75vh] p-4 space-y-6"
    >
      <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 border-b pb-2">
        {isNew ? "Add New Contact" : "Edit Contact Details"}
      </h1>

      {/* Core Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInputField
          label="Phone"
          name="customer_phonenumber"
          required
          value={formData.customer_phonenumber}
          onChange={handleChange}
          error={formErrors.customer_phonenumber}
        />
        <FormInputField
          label="Email"
          name="customer_email"
          type="email"
          value={formData.customer_email}
          onChange={handleChange}
          error={formErrors.customer_email}
        />
        <FormInputField
          label="First Name"
          name="customer_firstname"
          required
          value={formData.customer_firstname}
          onChange={handleChange}
          error={formErrors.customer_firstname}
        />
        <FormInputField
          label="Last Name"
          name="customer_lastname"
          required
          value={formData.customer_lastname}
          onChange={handleChange}
          error={formErrors.customer_lastname}
        />
      </div>

      {/* Program and Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col space-y-1">
          <label
            htmlFor="program"
            className="text-sm font-medium text-gray-700"
          >
            Program
          </label>
          <select
            id="program"
            name="program"
            value={formData.program || ""}
            onChange={handleChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 bg-white"
          >
            <option value="">Select a program</option>
            <option value="bacmune">bacmune</option>
            <option value="uromune">uromune</option>
            <option value="patient_support">patient_support</option>
          </select>
        </div>
        <FormInputField
          label="Address"
          name="customer_address"
          value={formData.customer_address}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <FormInputField
          label="City"
          name="customer_city"
          value={formData.customer_city}
          onChange={handleChange}
        />
        <FormInputField
          label="State"
          name="customer_state"
          value={formData.customer_state}
          onChange={handleChange}
        />
        <FormInputField
          label="Country"
          name="customer_country"
          value={formData.customer_country}
          onChange={handleChange}
        />
      </div>

      {/* Comments */}
      <div className="flex flex-col space-y-1">
        <label htmlFor="comments" className="text-sm font-medium text-gray-700">
          Comments
        </label>
        <textarea
          id="comments"
          name="customer_comments"
          value={formData.customer_comments || ""}
          onChange={handleChange}
          rows="3"
          className="p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-150 shadow-md"
        >
          <X className="w-5 h-5 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-[#2ba9cd] rounded-lg hover:bg-[#0b476c] focus:ring-4 focus:ring-[#0b476c] transition duration-150 shadow-lg"
        >
          <Save className="w-5 h-5 mr-2" />
          {isNew ? "Create Contact" : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

const ViewDetails = ({selectedContact, onEdit, onDelete, setIsModalOpen}) => {
  const contact = selectedContact;

  return (
    <div className="flex flex-col bg-white h-[75vh] overflow-y-auto p-4">
      {/* Header and Actions */}
      <div className="flex justify-between items-start border-b pb-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
            {contact.customer_firstname} {contact.customer_lastname}
          </h1>
          <p className="flex items-center text-lg text-[#2ba9cd] font-mono">
            {`+${contact.customer_phonenumber}`}
            <PhoneOutgoing
              className="w-5 h-5 ml-2 cursor-pointer text-red-500"
              onClick={() => setIsModalOpen(true)}
            />
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onEdit}
            className="p-2 text-sm text-[#2ba9cd] border border-[#2ba9cd] bg-[#E7F7FB] rounded-lg hover:bg-[#b0e2ef] transition duration-150 shadow-sm flex items-center"
            title="Edit Contact"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(contact.customer_phonenumber)}
            className="p-2 text-sm text-red-600 border border-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition duration-150 shadow-sm flex items-center"
            title="Delete Contact"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DetailItem
          icon={User}
          label="First Name"
          value={contact.customer_firstname}
        />
        <DetailItem
          icon={User}
          label="Last Name"
          value={contact.customer_lastname}
        />
        <DetailItem icon={Mail} label="Email" value={contact.customer_email} />
        <DetailItem icon={Tag} label="Program" value={contact.program} />
      </div>

      {/* Address Section */}
      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Location
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DetailItem
          icon={MapPin}
          label="Address"
          value={contact.customer_address}
        />
        <DetailItem icon={MapPin} label="City" value={contact.customer_city} />
        <DetailItem
          icon={MapPin}
          label="State"
          value={contact.customer_state}
        />
        <DetailItem
          icon={MapPin}
          label="Country"
          value={contact.customer_country}
        />
      </div>

      {/* Comments Section */}
      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Comments
      </h3>
      <div className="p-4 bg-[#E7F7FB] rounded-lg border border-[#2ba9cd]">
        <div className="flex items-start text-sm text-gray-700">
          <MessageCircle className="w-4 h-4 mr-2 text-[#2ba9cd] flex-shrink-0 mt-1" />
          <p>{contact.customer_comments || "No comments provided."}</p>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({isOpen, onClose, onConfirm, contact}) => {
  if (!isOpen || !contact) return null;

  const {customer_firstname, customer_lastname, customer_phonenumber} = contact;

  return (
    // Modal Overlay
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm transition-opacity duration-300">
      {/* Modal Card */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-11/12 max-w-md m-4 transform transition-all duration-300 scale-100 opacity-100"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
            <h3
              id="modal-title"
              className="text-xl font-bold text-gray-900 dark:text-white"
            >
              Confirm Deletion
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body Content */}
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Are you sure you want to permanently delete this contact? This
            action cannot be undone.
          </p>

          {/* Contact Details for Confirmation */}
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700/50">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1 flex items-center">
              <Trash2 className="w-4 h-4 mr-2" />
              Contact Details:
            </p>
            <ul className="list-none space-y-1 text-sm text-gray-700 dark:text-gray-200 ml-6">
              <li className="font-medium">
                Name:{" "}
                <span className="font-bold">
                  {customer_firstname} {customer_lastname}
                </span>
              </li>
              <li className="font-medium">
                Phone:{" "}
                <span className="font-mono text-gray-700 dark:text-gray-200 px-1 py-0.5 rounded text-sm">
                  <span className="font-bold">
                    +{`${customer_phonenumber}`}
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Please confirm your decision below.
          </p>
        </div>

        {/* Footer / Actions */}
        <div className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg shadow-md hover:bg-red-700 transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Contact
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactList = ({
  filteredContacts,
  searchTerm,
  selectedContact,
  setSearchTerm,
  handleAdd,
  setSelectedContact,
  setEditingContact,
}) => (
  <div className="flex flex-col bg-white border-r border-gray-200 overflow-y-auto">
    <div className="p-4 border-b">
      <h2 className="text-xl font-bold text-gray-800 flex items-center">
        <Phone className="w-5 h-5 mr-2 text-[#2ba9cd]" />
        Contact Directory
      </h2>

      <div className="relative mt-2 mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
        />
      </div>

      <button
        onClick={handleAdd}
        className="mt-2 w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition duration-150 shadow-md"
      >
        <Plus className="w-5 h-5 mr-1" />
        Add New Contact
      </button>
    </div>
    <div className="h-[55vh] overflow-y-auto overflow-x-auto">
      {filteredContacts.map((contact) => (
        <div
          key={contact.customer_phonenumber}
          className={`p-2 cursor-pointer transition duration-150 ease-in-out 
                                ${
                                  selectedContact &&
                                  selectedContact.customer_phonenumber ===
                                    contact.customer_phonenumber
                                    ? "bg-[#E7F7FB] border-[#2ba9cd] border-l-4"
                                    : "hover:bg-gray-50 border-l-4 border-[#E7F7FB]"
                                }`}
          onClick={() => {
            setSelectedContact(contact);
            setEditingContact(null);
          }}
        >
          <p className="font-semibold text-sm text-gray-500">
            {contact.customer_firstname} {contact.customer_lastname}
          </p>
          <p className="text-sm text-gray-400">
            +{contact.customer_phonenumber}
          </p>
        </div>
      ))}
      {filteredContacts.length === 0 && (
        <p className="p-4 text-center text-gray-500 text-sm italic">
          {searchTerm
            ? `No contacts match "${searchTerm}".`
            : 'No contacts found. Click "Add New Contact" to get started.'}
        </p>
      )}
    </div>
  </div>
);

// 2. Right Content Pane (Details or Form)
const ContentPane = ({
  editingContact,
  selectedContact,
  handleSave,
  handleCancel,
  handleEdit,
  handleDelete,
  setIsModalOpen,
  handleAdd,
}) => {
  if (editingContact) {
    return (
      <ContactForm
        contact={editingContact}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  if (selectedContact) {
    return (
      <ViewDetails
        selectedContact={selectedContact}
        onEdit={handleEdit}
        onDelete={handleDelete}
        setIsModalOpen={setIsModalOpen}
      />
    );
  }

  return (
    <div className="flex items-center justify-center text-gray-500">
      <div className="text-center p-8">
        <p className="text-lg mb-4">
          Select a contact from the list or add a new one.
        </p>
        <button
          onClick={handleAdd}
          className="flex items-center mx-auto px-4 py-2 text-sm font-medium text-white bg-[#2ba9cd] rounded-lg hover:bg-[#0b476c] transition duration-150 shadow-md"
        >
          <Plus className="w-5 h-5 mr-1" />
          Start Adding
        </button>
      </div>
    </div>
  );
};

const Contacts = (props) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState();
  const [editingContact, setEditingContact] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  // Set right before an add/edit save flips editingContact back to null, so
  // the effect below skips the refetch it would otherwise fire: contacts
  // state was already updated optimistically in handleSave, and refetching
  // immediately after the write raced the write itself, letting a stale
  // read silently replace the correct optimistic list (the new/edited
  // contact would then only reappear once something else triggered another
  // refetch). Delete doesn't set this flag - it has no optimistic local
  // update and genuinely relies on the refetch to drop the deleted contact.
  const skipNextFetchRef = useRef(false);

  const [queueOptions, setQueueOptions] = useState([
    {
      value: "",
      name: "--Please Select--",
    },
  ]);

  const fetchContacts = async (refresh = false) => {
    refresh === true && setIsLoading(true);
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };
      const resp = await fetch(
        `${process.env.REACT_APP_AWS_ENDPOINT}/get-customer-info`,
        options,
      );
      const data = await resp.json();
      console.log(
        `[CCP - Customer Fetched: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}] =====> ${data.length}`,
      );
      setContacts(data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let queues = props.agentQueues?.filter((x) => {
      return x && x !== null;
    });
    if (queues && queues.length > 0) {
      setQueueOptions([
        {
          value: "",
          name: "--Please Select--",
        },
        ...queues,
      ]);
    }

    fetchContacts(true);
  }, [props.agentQueues]);

  const filteredContacts = contacts.filter((contact) => {
    if (!searchTerm) return true; // Show all if search is empty

    const lowerCaseSearch = searchTerm.toLowerCase();

    return (
      contact.customer_phonenumber.toLowerCase().includes(lowerCaseSearch) ||
      contact.customer_firstname.toLowerCase().includes(lowerCaseSearch) ||
      contact.customer_lastname.toLowerCase().includes(lowerCaseSearch)
    );
  });

  useEffect(() => {
    if (!editingContact && deleteConfirmation === false) {
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
      } else {
        fetchContacts(false);
      }
    }
    // eslint-disable-next-line
  }, [editingContact, deleteConfirmation]);

  useEffect(() => {
    console.log(
      `Contacts Length: ${contacts.length}, Selected Contact: ${
        selectedContact ? selectedContact.customer_phonenumber : "None"
      }, Editing Contact: ${
        editingContact ? "Yes" : "No"
      }, Filtered Contacts Length: ${filteredContacts.length}`,
    );

    const isSelectedContactFilteredOut =
      selectedContact &&
      filteredContacts.every((c) => c.id !== selectedContact.id);

    if (contacts.length > 0 && !selectedContact && !editingContact) {
      if (filteredContacts.length > 0) {
        setSelectedContact(filteredContacts[0]);
      }
    } else if (isSelectedContactFilteredOut && filteredContacts.length > 0) {
      setSelectedContact(filteredContacts[0]);
    } else if (contacts.length === 0) {
      setSelectedContact(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, editingContact, filteredContacts]);

  const addContactHandler = async (contact) => {
    console.log(
      `[CCP - Customer Add: ${moment
        .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        .format("YYYY-MM-DD HH:mm:ss")}] =====> [
        Phone: ${contact.customer_phonenumber},
        Firstname: ${contact.customer_firstname}, 
        Lastname: ${contact.customer_lastname},
        Email: ${contact.customer_email}, 
        Address: ${contact.customer_address},
        City: ${contact.customer_city}, 
        State: ${contact.customer_state},
        Country: ${contact.customer_country}, 
        Comments: ${contact.customer_comments}
        Program: ${contact.customer_comments}
        ]`,
    );

    const options = {
      method: "PATCH",
      body: JSON.stringify({
        customer_phonenumber: `${contact.customer_phonenumber.replace(
          "+",
          "",
        )}`,
        customer_firstname: contact.customer_firstname,
        customer_lastname: contact.customer_lastname,
        customer_email: contact.customer_email,
        customer_address: contact.customer_address,
        customer_city: contact.customer_city,
        customer_state: contact.customer_state,
        customer_country: contact.customer_country,
        customer_comments: contact.customer_comments,
        program: contact.program,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_AUTH_TOKEN,
      },
    };

    const resp = await fetch(
      `${process.env.REACT_APP_AWS_ENDPOINT}/update-customer-info/customer`,
      options,
    );
    const data = await resp.json();
    console.log(
      `[CCP - Customer Add Result: ${moment
        .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(data)}`,
    );
    skipNextFetchRef.current = true;
    setEditingContact(null);
  };

  const deleteContactHandler = async (customer_phonenumber) => {
    const options = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_AUTH_TOKEN,
      },
    };

    const resp = await fetch(
      `${process.env.REACT_APP_AWS_ENDPOINT}/delete-customer-info/phone/${customer_phonenumber}`,
      options,
    );
    const data = await resp.json();
    console.log(
      `[CCP - Customer Delete: ${moment
        .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(data)}`,
    );
    setDeleteConfirmation(false);
    setEditingContact(null);
    setSelectedContact(null);
  };

  const handleAdd = () => {
    setSelectedContact(null);
    setEditingContact({
      customer_phonenumber: "",
      customer_firstname: "",
      customer_lastname: "",
      customer_email: "",
      customer_comments: "",
      customer_address: "",
      customer_city: "",
      customer_state: "",
      customer_country: "",
      program: "",
    });
  };

  const handleEdit = () => {
    setEditingContact(selectedContact);
  };

  const handleCancel = () => {
    setEditingContact(null);
    if (!selectedContact && contacts.length > 0) {
      setSelectedContact(contacts[0]);
    }
  };

  /* Add or update contact */
  const handleSave = (contactData) => {
    // Phone is a required field on the form, so customer_phonenumber is
    // always truthy here for both a brand-new contact and an edit - it
    // can't be used to tell them apart. What actually distinguishes them is
    // whether a contact with this phone number is already in local state.
    //
    // Also normalize the "+" here, once, so the locally-held contact
    // matches exactly what the backend persists (addContactHandler's PATCH
    // strips it before saving) - otherwise the optimistic copy keeps
    // whatever the user typed (e.g. "+917798006888") while the server
    // stores the digits-only form, and ViewDetails' `+${phone}` renders a
    // double "+" until the next refetch overwrites the local copy.
    const normalizedContact = {
      ...contactData,
      customer_phonenumber: contactData.customer_phonenumber.replace(/\+/g, ""),
    };

    setContacts((prev) => {
      const exists = prev.some(
        (c) => c.customer_phonenumber === normalizedContact.customer_phonenumber,
      );
      return exists
        ? prev.map((c) =>
            c.customer_phonenumber === normalizedContact.customer_phonenumber
              ? normalizedContact
              : c,
          )
        : [...prev, normalizedContact];
    });
    addContactHandler(normalizedContact);
    setSelectedContact(normalizedContact);
  };

  const handleDelete = () => {
    setDeleteConfirmation(true);
  };

  const handleSubmit = () => {
    setIsModalOpen(false);
    console.log(
      `Phone Number: ${selectedContact.customer_phonenumber}, Selected Queue: ${selectedQueue}`,
    );

    console.log(`Making Outbound Call!!`);
    props.makeDirectOutboundCall(
      selectedContact.customer_phonenumber,
      selectedQueue,
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const contactListProps = {
    filteredContacts,
    searchTerm,
    selectedContact,
    setSearchTerm,
    handleAdd,
    setSelectedContact,
    setEditingContact,
  };

  const contentPaneProps = {
    editingContact,
    selectedContact,
    handleSave,
    handleCancel,
    handleEdit,
    handleDelete,
    setIsModalOpen,
    handleAdd,
  };

  return (
    <div className="flex flex-col p-4 space-y-6">
      {isLoading === false ? (
        <div className="flex justify-between bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex w-full">
            <div className="w-1/3">
              <ContactList {...contactListProps} />
            </div>

            <div className="w-2/3 bg-white">
              <ContentPane {...contentPaneProps} />
            </div>
          </div>

          {/* The Modal */}
          <DeleteConfirmationModal
            isOpen={deleteConfirmation === true}
            onClose={() => setDeleteConfirmation(false)}
            onConfirm={() =>
              deleteContactHandler(selectedContact.customer_phonenumber)
            }
            contact={selectedContact}
          />

          {/* Modal Overlay */}
          {isModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
              onClick={closeModal}
            >
              {/* Modal Content Card */}
              <div
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-2xl font-semibold text-gray-800">
                    Outbound Call
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition"
                    aria-label="Close modal"
                  >
                    {/* Inline SVG X Icon */}
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>

                {/* Modal Body / Form */}
                <div className="p-6 space-y-6">
                  {/* Queue Dropdown */}
                  <div className="flex flex-col space-y-1">
                    <label
                      htmlFor="queue"
                      className="text-sm font-medium text-gray-700"
                    >
                      Target Queue
                    </label>
                    <select
                      id="queue"
                      value={selectedQueue}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      required
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 shadow-sm appearance-none bg-white pr-8"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='currentColor'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.75rem center",
                        backgroundSize: "1.5em 1.5em",
                      }}
                    >
                      <option value="" disabled>
                        Select a routing queue...
                      </option>
                      {queueOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Modal Footer / Actions */}
                  <div className="flex justify-end pt-4 space-x-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-150"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition duration-150 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!selectedQueue}
                      onClick={handleSubmit}
                    >
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center h-[75vh]">
          <OrbitLoader />
        </div>
      )}
    </div>
  );
};

export default React.memo(Contacts);
